<template>
	<div>
		<div class="col-md-12 hidden-print">
			<div class="row">
				<div class="col-md-4">
					<h3 class="page-header"><i class="fa fa-file-text-o animated bounceInDown show-info"></i> Salary Slip</h3>
				</div>
				<div class="col-md-8 text-right slip-actions" style="margin-top:20px">
					<template v-if="editing">
						<button class="btn btn-default" :disabled="saving" @click="cancelEdit">Cancel</button>
						<button class="btn btn-success" :disabled="saving" @click="save()">
							<i class="fa fa-check"></i> {{ saving ? 'Saving…' : 'Save changes' }}
						</button>
					</template>
					<template v-else>
						<button class="btn btn-default" @click="back">Back</button>
						<button class="btn btn-info" :disabled="!slip" @click="print"><i class="fa fa-print"></i> Print</button>
						<button class="btn btn-danger" :disabled="!slip || generatingPdf" @click="generatePdf">
							<i class="fa fa-file-pdf-o"></i> {{ generatingPdf ? 'Generating…' : 'PDF' }}
						</button>
						<button v-if="canEdit" class="btn btn-warning" :disabled="busy" @click="startEdit">
							<i class="fa fa-pencil"></i> Edit
						</button>
						<button v-if="slip && slip.status === 'draft'" class="btn btn-primary" :disabled="busy" @click="approve">
							<i class="fa fa-check"></i> {{ busy ? 'Working…' : 'Approve' }}
						</button>
						<button v-if="canRevert" class="btn btn-warning" :disabled="busy" @click="revert()">
							<i class="fa fa-undo"></i> Revert
						</button>
						<button
							v-if="slip && (slip.status === 'final' || slip.status === 'paid') && due > 0"
							class="btn btn-primary"
							@click="openPay"
						>
							<i class="fa fa-inr"></i> Record payment
						</button>
					</template>
				</div>
			</div>
		</div>

		<div class="col-md-12 hidden-print">
			<div v-if="error" class="alert alert-danger">{{ error }}</div>

			<!--
				Editing an approved slip is not a quiet act: its ledger entry is
				reversed and posted again. Say so before, not after.
			-->
			<div v-if="editing" class="alert alert-info">
				<div v-if="wasApproved">
					<strong>This slip is approved.</strong> Saving reverses its entry in the employee statement and
					posts the corrected figures, then approves it again. A reason is required.
				</div>
				<div v-else>Changes are saved together, and the server recalculates the totals.</div>
				<div style="margin-top:6px">
					Changing payable days or the divisor re-prorates every head paid per day. An amount you type
					yourself stays exactly as typed.
				</div>
			</div>

			<div v-if="laterApproved && !editing" class="alert alert-warning">
				<strong>{{ laterApproved }}</strong> already has an approved slip for a later month, computed from
				these figures. Changing this one will not change that one.
			</div>

			<div v-if="slip && slip.status === 'draft' && run && !run.att_lock_id" class="alert alert-warning">
				<strong>Attendance is not locked for this period.</strong> Approving here posts to the employee
				statement anyway, and the days underneath can still be changed afterwards.
			</div>

			<div v-if="activePayments.length && !editing" class="alert alert-warning">
				This slip has been paid. Reverse the payment below before editing it.
			</div>

			<div v-if="slip && slip.edited_at" class="alert alert-warning">
				<strong>Edited by hand.</strong> Some figures here were typed rather than worked out from
				attendance. Compute will ask before rebuilding this slip.
			</div>
		</div>

		<div v-if="slip" class="col-md-12">
			<div class="panel panel-cascade">
				<div ref="slipSheet" class="panel-body slip-sheet">
					<div class="slip-head">
						<h4 class="slip-company">V.S. DIGIWORLD</h4>
						<div>SHOP NO 16 GANJES MALL HARRIS GANJ KANPUR NAGAR</div>
						<h5 class="slip-title">Salary Slip</h5>
					</div>

					<section class="slip-section">
						<h5>Employee and slip details</h5>
						<div class="details-columns">
							<table class="table vertical-table"><tbody>
								<tr><th>Employee</th><td>{{ employee.name || '—' }}</td></tr>
								<tr><th>Employee code</th><td>{{ employee.employee_code || '—' }}</td></tr>
								<tr><th>Mobile</th><td>{{ employee.mobile || '—' }}</td></tr>
							</tbody></table>
							<table class="table vertical-table"><tbody>
								<tr><th>Slip number</th><td>{{ slip.slip_no || 'Draft' }}</td></tr>
								<tr>
									<th>Salary period</th>
									<td v-if="editing" class="edit-cell">
										<input v-model="form.period_start" type="date" class="form-control input-sm" />
										<input v-model="form.period_end" type="date" class="form-control input-sm" />
									</td>
									<td v-else>{{ formatDate(slip.period_start) }} – {{ formatDate(slip.period_end) }}</td>
								</tr>
								<tr><th>Status</th><td><span :class="slipStatusClass(slip.status)">{{ slipStatusLabel(slip.status) }}</span></td></tr>
							</tbody></table>
						</div>
					</section>

					<section class="slip-section">
						<h5>Attendance details</h5>
						<!-- Two columns: six stacked rows cost a third of the page on their own. -->
						<div class="details-columns">
							<table v-for="(column, index) in dayFieldColumns" :key="index" class="table vertical-table"><tbody>
								<tr v-for="field in column" :key="field.key">
									<th>{{ field.label }}</th>
									<td v-if="editing" class="edit-cell">
										<input
											v-model="form.days[field.key]"
											type="number"
											step="0.5"
											min="0"
											class="form-control input-sm text-right"
										/>
									</td>
									<td v-else>{{ days(slip[field.key]) }}</td>
								</tr>
							</tbody></table>
						</div>
						<div v-if="editing" class="edit-hint">
							Leave of pay days fill themselves in from the period unless you type them.
						</div>
					</section>

					<div v-if="lines.info.length" class="slip-info">
						<div v-for="line in lines.info" :key="line.id"><i class="fa fa-info-circle"></i> {{ line.name }} — {{ line.calc_note }}</div>
					</div>

					<div class="money-columns">
					<section v-for="block in blocks" :key="block.kind" v-show="showBlock(block)" class="slip-section">
						<h5>{{ block.title }}</h5>
						<table class="table vertical-table money-table">
							<thead><tr>
								<th>{{ block.head }}</th>
								<th class="text-right">Value</th>
								<th v-if="editing" class="edit-column"></th>
							</tr></thead>
							<tbody>
								<tr v-for="line in lines[block.kind]" :key="block.kind + line.id" :class="{ 'line-removed': isRemoved(line) }">
									<th>
										{{ line.name }}
										<small v-if="line.calc_note">{{ line.calc_note }}</small>
										<small v-if="line.original_amount !== null && line.original_amount !== undefined" class="edited-note">
											edited — was {{ money(line.original_amount) }}{{ line.override_reason ? ' · ' + line.override_reason : '' }}
										</small>
									</th>
									<td class="text-right">
										<input
											v-if="editing"
											v-model="form.amounts[line.id]"
											type="number"
											step="0.01"
											min="0"
											class="form-control input-sm text-right"
											:disabled="isRemoved(line)"
										/>
										<span v-else>{{ money(line.amount) }}</span>
									</td>
									<td v-if="editing" class="edit-column">
										<button class="btn btn-xs btn-default" @click="toggleRemove(line)">
											{{ isRemoved(line) ? 'Keep' : 'Remove' }}
										</button>
									</td>
								</tr>
								<tr v-for="(add, index) in addedIn(block.kind)" :key="'new' + index" class="line-added">
									<th>{{ add.name }}<small>{{ add.note || 'added by hand' }}</small></th>
									<td class="text-right">{{ money(add.amount) }}</td>
									<td v-if="editing" class="edit-column">
										<button class="btn btn-xs btn-default" @click="dropAdded(add)">Remove</button>
									</td>
								</tr>
								<tr v-if="!lines[block.kind].length && !addedIn(block.kind).length">
									<td :colspan="editing ? 3 : 2" class="text-center text-muted">{{ block.empty }}</td>
								</tr>
								<tr v-if="block.total" class="total-row">
									<th>{{ block.total }}</th>
									<td class="text-right">{{ money(slip[block.totalField]) }}</td>
									<td v-if="editing" class="edit-column"></td>
								</tr>
							</tbody>
						</table>
					</section>

					<section class="slip-section summary-section">
						<h5>Salary summary</h5>
						<!-- Gross and deductions already carry their own total rows above. -->
						<table class="table vertical-table money-table"><tbody>
							<tr><th>Net payable</th><td class="text-right">{{ money(slip.net_payable) }}</td></tr>
							<tr v-if="lines.recoveries.length"><th>Amount after recoveries</th><td class="text-right">{{ money(slip.amount_payable) }}</td></tr>
							<!-- Only once something has actually been paid: on an unpaid slip
							     "still due" only repeats the payable figure above it. -->
							<tr v-if="slip.paid_amount > 0"><th>Amount paid</th><td class="text-right">{{ money(slip.paid_amount) }}</td></tr>
							<tr v-if="slip.paid_amount > 0 && due > 0"><th>Amount still due</th><td class="text-right">{{ money(due) }}</td></tr>
							<tr v-if="slip.rounding != 0"><th>Rounding adjustment</th><td class="text-right">{{ money(slip.rounding) }}</td></tr>
						</tbody></table>
						<div v-if="editing" class="edit-hint">These totals are recalculated by the server when you save.</div>
					</section>
					</div>

					<section v-if="editing" class="slip-section hidden-print">
						<h5>Add a line</h5>
						<div class="add-line">
							<select v-model="newLine.component_id" class="form-control input-sm">
								<option value="">Choose a salary head…</option>
								<option v-for="component in components" :key="component.id" :value="component.id">
									{{ component.name }} ({{ component.kind }})
								</option>
							</select>
							<input v-model="newLine.amount" type="number" step="0.01" min="0" class="form-control input-sm" placeholder="Amount" />
							<input v-model="newLine.note" type="text" class="form-control input-sm" placeholder="Why (printed on the slip)" />
							<button class="btn btn-sm btn-default" @click="addLine">Add</button>
						</div>
					</section>

					<section v-if="activePayments.length" class="slip-section">
						<h5>Payments</h5>
						<table class="table vertical-table money-table">
							<thead><tr>
								<th>Paid on</th>
								<th class="text-right">Amount</th>
								<th class="edit-column hidden-print"></th>
							</tr></thead>
							<tbody>
								<tr v-for="payment in activePayments" :key="payment.id">
									<th>{{ formatDate(payment.paid_on) }}</th>
									<td class="text-right">{{ money(payment.amount) }}</td>
									<td class="edit-column hidden-print">
										<button class="btn btn-xs btn-default" :disabled="busy" @click="reversePayment(payment)">
											Reverse
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</section>

					<div v-if="editing && wasApproved" class="slip-section hidden-print">
						<label>Reason for this change (required)</label>
						<input v-model="form.reason" type="text" class="form-control" maxlength="200" placeholder="e.g. LOP corrected after leave approval" />
					</div>

					<div class="slip-signatures">
						<div>Employee signature</div><div>Authorised signatory</div>
					</div>
					<div class="generated-at">Generated {{ generatedAt }}</div>
				</div>
			</div>
		</div>

		<modal name="payroll-pay" height="auto" :adaptive="true" width="420">
			<div v-if="slip" style="padding:20px">
				<h4 class="modal-header-title">Record payment</h4>
				<p class="text-muted">{{ employee.name }} · {{ money(due) }} outstanding on {{ slip.slip_no || 'this slip' }}</p>
				<div class="form-group"><label>Amount</label><input v-model="payForm.amount" type="number" class="form-control" /></div>
				<div class="form-group"><label>Paid on</label><input v-model="payForm.paid_on" type="date" class="form-control" /></div>
				<div class="form-group"><label>Note</label><input v-model="payForm.narration" type="text" class="form-control" /></div>
				<div v-if="payError" class="alert alert-danger">{{ payError }}</div>
				<div class="text-right">
					<button class="btn btn-default" @click="$modal.hide('payroll-pay')">Cancel</button>
					<button class="btn btn-primary" style="margin-left:6px" :disabled="paying" @click="pay">{{ paying ? 'Saving…' : 'Record' }}</button>
				</div>
			</div>
		</modal>

		<!-- Reversal touches the statement; nothing may be pressed until it is done. -->
		<div v-if="busy" class="slip-overlay">
			<div class="slip-overlay-box">
				<i class="fa fa-spinner fa-spin fa-2x"></i>
				<div style="margin-top:12px"><strong>{{ busyLabel }}</strong></div>
				<div class="text-muted" style="font-size:12px">Updating the employee statement. Please wait.</div>
			</div>
		</div>
	</div>
</template>

<script>
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { slipService, componentService, errorMessage } from './payrollService'
import payrollFormat from './payrollFormat'

const DAY_FIELDS = [
	{ key: 'period_days', label: 'Period days' },
	{ key: 'payable_days', label: 'Payable days' },
	{ key: 'lop_days', label: 'Loss of pay days' },
	{ key: 'present_days', label: 'Present days' },
	{ key: 'leave_days', label: 'Leave days' },
	{ key: 'day_divisor', label: 'Salary day divisor' },
]

const BLOCKS = [
	{ kind: 'earnings', title: 'Earnings', head: 'Salary head', empty: 'No earnings', total: 'Gross earnings', totalField: 'gross_earnings' },
	{ kind: 'deductions', title: 'Deductions', head: 'Deduction head', empty: 'No deductions', total: 'Total deductions', totalField: 'total_deductions' },
	{ kind: 'recoveries', title: 'Recoveries', head: 'Recovery head', empty: 'No recoveries', total: '', totalField: '' },
]

/**
 * One slip, and everything that can be done to it.
 *
 * Nothing here works out money. Amounts typed in edit mode are sent as typed
 * and the server re-totals, which is why the summary says so rather than
 * quietly adding up in the browser — the failure this module was written to
 * replace was exactly that.
 */
export default {
	name: 'PayrollSlip',
	mixins: [payrollFormat],
	data() {
		return {
			slip: null,
			run: null,
			employee: {},
			lines: { info: [], earnings: [], deductions: [], recoveries: [] },
			payments: [],
			laterApproved: '',
			components: [],
			error: '',
			editing: false,
			wasApproved: false,
			saving: false,
			busy: false,
			busyLabel: '',
			paying: false,
			generatingPdf: false,
			generatedAt: '',
			payError: '',
			payForm: { amount: 0, paid_on: this.toIsoDate(new Date()), narration: '' },
			// Held between the first attempt and the confirmed retry.
			pendingReason: '',
			form: { period_start: '', period_end: '', days: {}, amounts: {}, removed: [], added: [], reason: '' },
			newLine: { component_id: '', amount: '', note: '' },
			dayFields: DAY_FIELDS,
			blocks: BLOCKS,
		}
	},
	computed: {
		due() {
			if (!this.slip) return 0
			return Math.max(0, Number(this.slip.amount_payable || 0) - Number(this.slip.paid_amount || 0))
		},
		activePayments() {
			return this.payments.filter((payment) => !payment.reversed)
		},
		/** A cancelled slip is reopened before it can be touched; a paid one is refused by the server. */
		canEdit() {
			return !!this.slip && this.slip.status !== 'cancelled'
		},
		canRevert() {
			return !!this.slip && ['final', 'paid', 'cancelled'].includes(this.slip.status)
		},
		allLines() {
			return [...this.lines.earnings, ...this.lines.deductions, ...this.lines.recoveries]
		},
		/** The day figures, halved so they print side by side. */
		dayFieldColumns() {
			const half = Math.ceil(this.dayFields.length / 2)
			return [this.dayFields.slice(0, half), this.dayFields.slice(half)]
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		refresh() {
			this.error = ''
			return slipService
				.show(this.$route.params.id)
				.then((data) => {
					this.slip = data.slip
					this.run = data.run || null
					this.employee = data.employee || {}
					this.lines = data.lines || this.lines
					this.payments = data.payments || []
					this.laterApproved = data.later_approved || ''
					this.generatedAt = new Date().toLocaleString()
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load that slip.')
				})
		},

		// ---------------------------------------------------------------
		// Editing
		// ---------------------------------------------------------------

		startEdit() {
			const days = {}
			DAY_FIELDS.forEach((field) => {
				days[field.key] = Number(this.slip[field.key] || 0)
			})
			const amounts = {}
			this.allLines.forEach((line) => {
				amounts[line.id] = Number(line.amount)
			})

			this.form = {
				period_start: this.dateValue(this.slip.period_start),
				period_end: this.dateValue(this.slip.period_end),
				days,
				amounts,
				removed: [],
				added: [],
				reason: '',
			}
			this.newLine = { component_id: '', amount: '', note: '' }
			this.wasApproved = this.slip.status === 'final' || this.slip.status === 'paid'
			this.editing = true
			this.error = ''

			if (!this.components.length) {
				componentService.list().then((rows) => {
					this.components = rows || []
				}).catch(() => {
					this.components = []
				})
			}
		},
		cancelEdit() {
			this.editing = false
			this.form.added = []
			this.form.removed = []
			this.form.reason = ''
			this.error = ''
		},
		/** Recoveries only earn their half of the row when there are any. */
		showBlock(block) {
			return block.kind !== 'recoveries' || this.lines.recoveries.length > 0 || this.editing
		},
		isRemoved(line) {
			return this.form.removed.includes(line.id)
		},
		toggleRemove(line) {
			this.form.removed = this.isRemoved(line)
				? this.form.removed.filter((id) => id !== line.id)
				: this.form.removed.concat(line.id)
		},
		/**
		 * Lines waiting to be saved, shown in place among the real ones. Empty
		 * unless editing: once saved they come back from the server as real
		 * lines, and a leftover preview would show the same line twice.
		 */
		addedIn(kind) {
			if (!this.editing) return []
			const wanted = { earnings: 'earning', deductions: 'deduction', recoveries: 'recovery' }[kind]
			return this.form.added.filter((add) => add.kind === wanted)
		},
		addLine() {
			const component = this.components.find((row) => String(row.id) === String(this.newLine.component_id))
			const amount = Number(this.newLine.amount)
			if (!component || !(amount > 0)) {
				this.$notify({ text: 'Choose a salary head and an amount above zero.', type: 'warn' })
				return
			}
			this.form.added.push({
				component_id: component.id,
				name: component.name,
				kind: component.kind,
				amount,
				note: this.newLine.note,
			})
			this.newLine = { component_id: '', amount: '', note: '' }
		},
		dropAdded(add) {
			this.form.added = this.form.added.filter((row) => row !== add)
		},
		/** Only what actually changed, so an untouched field is never sent as an edit. */
		buildPayload() {
			const payload = {}

			if (this.form.period_start && this.form.period_start !== this.dateValue(this.slip.period_start)) {
				payload.period_start = this.form.period_start
			}
			if (this.form.period_end && this.form.period_end !== this.dateValue(this.slip.period_end)) {
				payload.period_end = this.form.period_end
			}

			const days = {}
			DAY_FIELDS.forEach((field) => {
				const next = Number(this.form.days[field.key])
				if (Number.isFinite(next) && next !== Number(this.slip[field.key] || 0)) days[field.key] = next
			})
			if (Object.keys(days).length) payload.days = days

			const lines = []
			this.allLines.forEach((line) => {
				if (this.isRemoved(line)) return
				const next = Number(this.form.amounts[line.id])
				if (Number.isFinite(next) && Math.abs(next - Number(line.amount)) > 0.004) {
					lines.push({ id: line.id, amount: next })
				}
			})
			this.form.added.forEach((add) => {
				lines.push({ component_id: add.component_id, amount: Number(add.amount), note: add.note || undefined })
			})
			if (lines.length) payload.lines = lines
			if (this.form.removed.length) payload.remove_line_ids = this.form.removed

			return payload
		},
		// `confirmed` is only ever true on the deliberate retry after the warning:
		// a bare @click hands a method the MouseEvent, which is truthy.
		save(confirmLaterApproved) {
			const confirmed = confirmLaterApproved === true
			const payload = this.buildPayload()

			if (!Object.keys(payload).length) {
				this.$notify({ text: 'Nothing has changed.', type: 'warn' })
				return
			}
			if (this.wasApproved && !this.form.reason.trim()) {
				this.$notify({ text: 'Give a reason: this slip is approved.', type: 'warn' })
				return
			}
			if (this.form.reason.trim()) payload.reason = this.form.reason.trim()
			if (confirmed) payload.confirm_later_approved = true

			this.saving = true
			this.busy = this.wasApproved
			this.busyLabel = 'Saving…'
			this.error = ''

			slipService
				.update(this.$route.params.id, payload)
				.then(() => {
					this.editing = false
					this.form.added = []
					this.form.removed = []
					this.form.reason = ''
					this.$notify({
						text: this.wasApproved
							? 'Saved. The statement was reversed and posted again.'
							: 'Saved.',
						type: 'success',
					})
					return this.refresh()
				})
				.catch((e) => this.handleLaterApproved(e, 'Could not save this slip.', () => this.save(true)))
				.finally(() => {
					this.saving = false
					this.busy = false
				})
		},

		// ---------------------------------------------------------------
		// Approve, revert, pay
		// ---------------------------------------------------------------

		approve() {
			const unlocked = this.run && !this.run.att_lock_id
			if (
				!window.confirm(
					'Approve this slip? It is numbered and posted to the employee statement.' +
						(unlocked
							? '\n\nAttendance for this period is NOT locked, so the days behind these figures can still change.'
							: '')
				)
			) {
				return
			}

			this.busy = true
			this.busyLabel = 'Approving…'
			this.error = ''
			slipService
				.approve(this.$route.params.id)
				.then(() => {
					this.$notify({ text: 'Approved and posted to the statement.', type: 'success' })
					return this.refresh()
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not approve this slip.')
				})
				.finally(() => {
					this.busy = false
				})
		},
		revert(confirmLaterApproved) {
			const confirmed = confirmLaterApproved === true
			let reason = this.pendingReason
			if (!confirmed) {
				reason = window.prompt(
					'Revert this slip to draft? Its entry in the employee statement is reversed' +
						(this.activePayments.length ? ', along with the payments recorded against it' : '') +
						'.\n\nReason (required):'
				)
				if (reason === null) return
				if (!reason.trim()) {
					this.$notify({ text: 'A reason is required to revert.', type: 'warn' })
					return
				}
				this.pendingReason = reason.trim()
			}

			this.busy = true
			this.busyLabel = 'Reverting…'
			this.error = ''
			slipService
				.reopen(this.$route.params.id, this.pendingReason, confirmed)
				.then(() => {
					this.$notify({ text: 'Reverted to draft.', type: 'success' })
					return this.refresh()
				})
				.catch((e) => this.handleLaterApproved(e, 'Could not revert this slip.', () => this.revert(true)))
				.finally(() => {
					this.busy = false
				})
		},
		reversePayment(payment) {
			const reason = window.prompt(
				`Reverse the payment of ${this.money(payment.amount)}? The entry stays on record marked ` +
					'reversed, and an opposite row goes to the statement.\n\nReason (required):'
			)
			if (reason === null) return
			if (!reason.trim()) {
				this.$notify({ text: 'A reason is required to reverse a payment.', type: 'warn' })
				return
			}

			this.busy = true
			this.busyLabel = 'Reversing…'
			this.error = ''
			slipService
				.reversePayment(payment.id, reason.trim())
				.then(() => {
					this.$notify({ text: 'Payment reversed.', type: 'success' })
					return this.refresh()
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not reverse that payment.')
				})
				.finally(() => {
					this.busy = false
				})
		},
		openPay() {
			this.payError = ''
			this.payForm = { amount: this.due, paid_on: this.toIsoDate(new Date()), narration: '' }
			this.$modal.show('payroll-pay')
		},
		pay() {
			this.paying = true
			this.payError = ''
			slipService
				.pay(this.$route.params.id, this.payForm)
				.then(() => {
					this.$notify({ text: 'Payment recorded.', type: 'success' })
					this.$modal.hide('payroll-pay')
					this.refresh()
				})
				.catch((e) => {
					this.payError = errorMessage(e, 'Could not record that payment.')
				})
				.finally(() => {
					this.paying = false
				})
		},

		// ---------------------------------------------------------------
		// Plumbing
		// ---------------------------------------------------------------

		/**
		 * The server refuses once, naming the later approved month, and accepts
		 * the same call again when the answer is yes. A refusal is never a dead
		 * end, and never a silent override either.
		 */
		handleLaterApproved(e, fallback, retry) {
			const errors = e && e.response && e.response.data && e.response.data.errors
			const warning = errors && errors.later_approved && errors.later_approved[0]

			if (warning && window.confirm(warning + '\n\nGo ahead anyway?')) {
				retry()
				return
			}

			this.error = warning || errorMessage(e, fallback)
		},
		dateValue(value) {
			return value ? String(value).slice(0, 10) : ''
		},
		/**
		 * The app shell - top bar, navigation, the idle-timer box - prints too,
		 * and on its own pushes the slip onto a second sheet. The body class
		 * hides everything but the slip for the length of the dialog, and is
		 * scoped to this screen so no other page's printing changes.
		 */
		print() {
			document.body.classList.add('payslip-printing')
			try {
				window.print()
			} finally {
				document.body.classList.remove('payslip-printing')
			}
		},
		async generatePdf() {
			if (!this.$refs.slipSheet) return
			this.generatingPdf = true
			this.error = ''
			this.generatedAt = new Date().toLocaleString()
			try {
				await this.$nextTick()
				const canvas = await html2canvas(this.$refs.slipSheet, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
				const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
				const pageWidth = pdf.internal.pageSize.getWidth()
				const pageHeight = pdf.internal.pageSize.getHeight()
				const margin = 8
				const image = canvas.toDataURL('image/jpeg', 0.95)
				// Fit the sheet to the page rather than slicing it across pages:
				// a salary slip is one sheet of paper, whoever it is for.
				// Fit the width so the type keeps its size; height only takes
				// over if a long slip would otherwise run off the page.
				const scale = Math.min(
					(pageWidth - margin * 2) / canvas.width,
					(pageHeight - margin * 2) / canvas.height
				)
				const imageWidth = canvas.width * scale
				const imageHeight = canvas.height * scale
				pdf.addImage(image, 'JPEG', (pageWidth - imageWidth) / 2, margin, imageWidth, imageHeight)
				const number = String(this.slip.slip_no || this.slip.id || 'salary-slip').replace(/[^a-z0-9-]+/gi, '-')
				pdf.save('salary-slip-' + number + '.pdf')
			} catch (e) {
				this.error = 'Could not generate the PDF. Please try again.'
			} finally {
				this.generatingPdf = false
			}
		},
		back() {
			if (this.slip && this.slip.run_id) {
				this.$router.push({ name: 'payroll-run', params: { id: this.slip.run_id } })
				return
			}
			this.$router.push({ name: 'payroll-runs' })
		},
	},
}
</script>

<!-- Not scoped: these must reach the app shell around the slip. Inert unless
     PaySlip has put `payslip-printing` on the body, which it only does while the
     print dialog is open. -->
<style>
@media print {
	body.payslip-printing * {
		visibility: hidden;
	}
	body.payslip-printing .slip-sheet,
	body.payslip-printing .slip-sheet * {
		visibility: visible;
	}
	body.payslip-printing .slip-sheet {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
	}
	body.payslip-printing .hidden-print {
		display: none !important;
	}
}
</style>

<style scoped>
.slip-actions .btn{margin-left:6px}.slip-sheet{background:#fff;padding:24px 30px 18px;max-width:850px;margin:0 auto;color:#222}.slip-head{text-align:center;border-bottom:2px solid #475c78;padding-bottom:12px}.slip-company{font-size:26px;font-weight:700;color:#1c304a;margin:0 0 4px}.slip-title{margin:10px 0 0;letter-spacing:.08em;text-transform:uppercase;font-size:14px}.slip-section{margin-top:14px}.slip-section>h5{background:#475c78;color:#fff;padding:7px 10px;margin:0;text-transform:uppercase;font-size:11px;letter-spacing:.05em}.details-columns{display:flex;gap:14px}.details-columns .vertical-table{width:50%}.vertical-table{margin:0;border:1px solid #ccd3dc}.vertical-table th,.vertical-table td{padding:7px 10px!important;border-top:1px solid #dde2e8!important;vertical-align:top}.vertical-table th{width:58%;background:#f4f6f8;color:#364152;font-weight:600}.vertical-table td{width:42%;text-align:right}.money-table thead th{background:#e9edf2;color:#27364a}.money-table th small{display:block;color:#7a8493;font-weight:400;font-size:10px;margin-top:2px}.total-row th,.total-row td{font-weight:700;border-top:2px solid #9ba6b5!important}.summary-section .vertical-table th,.summary-section .vertical-table td{font-size:14px}.summary-section .vertical-table tr:nth-last-child(-n+3) th,.summary-section .vertical-table tr:nth-last-child(-n+3) td{font-weight:700}.slip-info{font-size:11px;color:#8a6d2f;background:#fff8e6;padding:7px 10px;margin-top:10px}.slip-signatures{display:flex;justify-content:space-between;margin-top:42px;padding-top:6px}.slip-signatures>div{width:180px;border-top:1px solid #555;text-align:center;font-size:11px}.generated-at{text-align:right;color:#777;font-size:9px;margin-top:12px}.text-right{text-align:right}.text-center{text-align:center}.modal-header-title{margin-top:0}@media(max-width:767px){.slip-actions .btn{margin-top:5px}.slip-sheet{padding:15px}.details-columns{display:block}.details-columns .vertical-table{width:100%;margin-bottom:10px}}
/* Print: the slip at its own size, one A4 page. */
@media print {
	@page {
		size: A4;
		margin: 10mm;
	}
	.hidden-print { display: none !important; }
	.panel { border: 0 !important; box-shadow: none !important; }
	.slip-sheet { padding: 0; max-width: none; }
	.slip-section { break-inside: avoid; }
	.generated-at { display: none; }
}

/* Earnings beside Deductions, Recoveries beside the summary. */
.money-columns {
	display: flex;
	flex-wrap: wrap;
	gap: 14px;
}
.money-columns > section {
	flex: 1 1 calc(50% - 7px);
	min-width: 0;
}
@media (max-width: 767px) {
	.money-columns > section {
		flex-basis: 100%;
	}
}

/* Edit mode */
.edit-cell input {
	display: inline-block;
	width: auto;
	max-width: 120px;
	text-align: right;
}
.edit-cell input + input {
	margin-top: 4px;
}
.edit-column {
	width: 90px;
	text-align: center !important;
}
.edit-hint {
	font-size: 11px;
	color: #7a8493;
	padding: 4px 2px;
}
.edited-note {
	color: #b58a2b !important;
}
.line-removed th,
.line-removed td {
	text-decoration: line-through;
	opacity: 0.55;
}
.line-added th,
.line-added td {
	background: #f1f8f2;
}
.add-line {
	display: flex;
	gap: 8px;
	align-items: center;
	padding: 8px 2px;
	flex-wrap: wrap;
}
.add-line select {
	min-width: 220px;
}
.add-line input {
	max-width: 220px;
}
.slip-overlay {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.75);
}
.slip-overlay-box {
	text-align: center;
	background: #fff;
	border: 1px solid #ddd;
	border-radius: 6px;
	padding: 24px 32px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}
</style>
